import { DSCircularButton } from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms/DSModal";
import type { NoteDisplayData } from "@components/design-system/organisms/DSNoteDisplay";
import { DSNoteInput } from "@components/design-system/organisms/DSNoteInput";
import type { NoteSection } from "@components/design-system/organisms/DSNoteInput";
import { useFindAndReplace } from "@hooks";
import { useCompanion } from "@hooks/use-companion";
import { usePlainNoteEditor } from "@hooks/use-plain-note-editor";
import { useWritingAssistant } from "@hooks/use-writing-assistant";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useFormContext, useWatch } from "react-hook-form";

import { languageCodeToLanguageName } from "./utils";
import { UserPermissions } from "../../../../hooks/use-premium-feature";
import Twemoji from "../../../../Twemoji";
import CascadingDropdown from "../../../common/menus/CascadingDropdown";
import PremiumFeatureGate from "../../../common/premium/PremiumFeatureGate";
import UpgradeModal from "../../../common/premium/UpgradeModal";
import CustomizationFeedback from "../../common/CustomizationFeedback";

const SECTIONS: NoteSection[] = [
  "subjective",
  "objective",
  "assessment",
  "plan",
  "additional",
];

const SECTION_FIELD_NAMES: Record<string, string> = {
  subjective: "subjective",
  objective: "objective",
  assessment: "assessment",
  plan: "plan",
  additional: "additionalNotes",
};

const SECTION_LABELS: Record<string, string> = {
  subjective: "Subjective",
  objective: "Objective",
  assessment: "Assessment",
  plan: "Plan",
  additional: "Additional Notes",
};

interface SOAPFormProps {
  note: {
    uuid: string;
    [key: string]: unknown;
  };
}

export default function SOAPForm({ note }: SOAPFormProps) {
  const plainText = usePlainNoteEditor();
  const { control, setValue, getValues } = useFormContext();
  const {
    aiAssistantEnabled,
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
    writingCompanion,
  } = useFlags();
  const { expandSidebar, createNewSession, creatingSession } =
    useCompanion();
  const { selectFindAndReplaceText } = useFindAndReplace();

  // Watch all SOAP fields for reactive DSNoteInput data
  const subjective = useWatch({ control, name: "subjective" });
  const objective = useWatch({ control, name: "objective" });
  const assessment = useWatch({ control, name: "assessment" });
  const plan = useWatch({ control, name: "plan" });
  const additionalNotes = useWatch({
    control,
    name: "additionalNotes",
  });

  const noteData: NoteDisplayData = {
    subjective: subjective || "",
    objective: objective || "",
    assessment: assessment || "",
    plan: plan || "",
    additionalNotes: additionalNotes || "",
  };

  const handleChange = (newData: NoteDisplayData) => {
    for (const key of Object.keys(newData)) {
      if (
        noteData[key as keyof NoteDisplayData] !==
        newData[key as keyof NoteDisplayData]
      ) {
        setValue(key, newData[key as keyof NoteDisplayData], {
          shouldDirty: true,
        });
      }
    }
  };

  // Hooks must be called unconditionally — one per section
  const waSubjective = useWritingAssistant({
    noteUuid: note?.uuid,
    fieldName: "subjective",
    getFieldValue: () => getValues("subjective"),
    setFieldValue: (v) =>
      setValue("subjective", v, { shouldDirty: true }),
    getOtherFieldValue: (fn) => getValues(fn),
    setOtherFieldValue: (fn, v) =>
      setValue(fn, v, { shouldDirty: true }),
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  });

  const waObjective = useWritingAssistant({
    noteUuid: note?.uuid,
    fieldName: "objective",
    getFieldValue: () => getValues("objective"),
    setFieldValue: (v) =>
      setValue("objective", v, { shouldDirty: true }),
    getOtherFieldValue: (fn) => getValues(fn),
    setOtherFieldValue: (fn, v) =>
      setValue(fn, v, { shouldDirty: true }),
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  });

  const waAssessment = useWritingAssistant({
    noteUuid: note?.uuid,
    fieldName: "assessment",
    getFieldValue: () => getValues("assessment"),
    setFieldValue: (v) =>
      setValue("assessment", v, { shouldDirty: true }),
    getOtherFieldValue: (fn) => getValues(fn),
    setOtherFieldValue: (fn, v) =>
      setValue(fn, v, { shouldDirty: true }),
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  });

  const waPlan = useWritingAssistant({
    noteUuid: note?.uuid,
    fieldName: "plan",
    getFieldValue: () => getValues("plan"),
    setFieldValue: (v) => setValue("plan", v, { shouldDirty: true }),
    getOtherFieldValue: (fn) => getValues(fn),
    setOtherFieldValue: (fn, v) =>
      setValue(fn, v, { shouldDirty: true }),
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  });

  const waAdditional = useWritingAssistant({
    noteUuid: note?.uuid,
    fieldName: "additionalNotes",
    getFieldValue: () => getValues("additionalNotes"),
    setFieldValue: (v) =>
      setValue("additionalNotes", v, { shouldDirty: true }),
    getOtherFieldValue: (fn) => getValues(fn),
    setOtherFieldValue: (fn, v) =>
      setValue(fn, v, { shouldDirty: true }),
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  });

  const writingAssistants: Record<
    string,
    ReturnType<typeof useWritingAssistant>
  > = {
    subjective: waSubjective,
    objective: waObjective,
    assessment: waAssessment,
    plan: waPlan,
    additionalNotes: waAdditional,
  };

  // Show a single upgrade modal controlled by whichever WA triggered it
  const showingUpgradeWA = [
    waSubjective,
    waObjective,
    waAssessment,
    waPlan,
    waAdditional,
  ].find((wa) => wa.showUpgradeModal);

  return (
    <>
      <UpgradeModal
        shouldShow={!!showingUpgradeWA}
        setShouldShow={(val: boolean) =>
          showingUpgradeWA?.setShowUpgradeModal(val)
        }
        featureName="Writing Assistant"
      />

      {/* Translation modal — only relevant for subjective section */}
      <DSModal
        isOpen={waSubjective.translationModal.isShowing}
        onClose={waSubjective.translationModal.onDismiss}
        title="Translation Detected"
        actions={[
          {
            label: "Only Translate Subjective",
            variant: "cancel",
            onClick: waSubjective.translationModal.onDismiss,
          },
          {
            label: "Translate All",
            variant: "primary",
            onClick: waSubjective.translationModal.onTranslateAll,
            dataTestId: "confirm-button",
          },
        ]}
        dataTestId="translation-modal"
      >
        <h5 className="text-sm">
          We noticed that you translated the Subjective section of the
          note to{" "}
          {languageCodeToLanguageName(
            waSubjective.translationModal.language,
          )}
          . Would you like the rest of your note to be translated to{" "}
          {languageCodeToLanguageName(
            waSubjective.translationModal.language,
          )}
          ?
        </h5>
      </DSModal>

      <DSNoteInput
        data={noteData}
        onChange={handleChange}
        dataTestId="note-input"
        onMouseUp={selectFindAndReplaceText}
        plainText={plainText}
      >
        {SECTIONS.map((section) => {
          const fieldName = SECTION_FIELD_NAMES[section];
          const wa = writingAssistants[fieldName];

          const aiActions =
            aiAssistantEnabled && !wa.diffState ? (
              <>
                <PremiumFeatureGate
                  featureName="Writing Assistant"
                  requiredPermission={
                    UserPermissions.WRITING_ASSISTANT_ENABLED
                  }
                  iconPosition="center"
                  iconSize="xs"
                >
                  <CascadingDropdown
                    options={wa.options}
                    setIsMenuOpen={wa.setIsMenuOpen}
                    title={SECTION_LABELS[section]}
                    isTriggerComponentDisabled={
                      wa.loadingBeforeAndDuringStreaming
                    }
                    triggerComponent={({
                      onClick,
                    }: {
                      onClick: () => void;
                    }) => (
                      <DSCircularButton
                        onClick={onClick}
                        size="sm"
                        disabled={wa.loadingBeforeAndDuringStreaming}
                        title="Trigger Writing Assistant"
                        dataTestId="cascading-dropdown-trigger"
                      >
                        <Twemoji
                          className="text-xl z-20"
                          emoji="✨"
                        />
                      </DSCircularButton>
                    )}
                  />
                </PremiumFeatureGate>
                {writingCompanion && (
                  <PremiumFeatureGate
                    featureName="Companion"
                    requiredPermission={undefined}
                    iconPosition="center"
                    iconSize="xs"
                  >
                    <DSCircularButton
                      size="sm"
                      disabled={creatingSession}
                      onClick={() => {
                        if (creatingSession) return;
                        expandSidebar();
                        createNewSession();
                      }}
                      title="Edit with Quill"
                      dataTestId="edit-with-quill-trigger"
                    >
                      <Twemoji className="text-xl z-20" emoji="🦔" />
                    </DSCircularButton>
                  </PremiumFeatureGate>
                )}
              </>
            ) : undefined;

          return (
            <div key={section}>
              {wa.diffState ? (
                <DSNoteInput.NoteField
                  section={section}
                  showSparkleButton={false}
                  showCopyButton
                  actions={aiActions}
                  diffState={wa.diffState}
                  onDiffAccept={wa.acceptChanges}
                  onDiffReject={wa.rejectChanges}
                />
              ) : (
                <DSNoteInput.NoteField
                  section={section}
                  showSparkleButton={false}
                  showCopyButton
                  actions={aiActions}
                />
              )}

              {/* Customization feedback */}
              {wa.isShowingExtraContent &&
                wa.customizationFeedback.hasFeedbackAccess && (
                  <CustomizationFeedback
                    llmCustomizationUuid={
                      wa.customizationFeedback.llmCustomizationUuid
                    }
                  />
                )}
            </div>
          );
        })}
      </DSNoteInput>
    </>
  );
}
